import { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';

import { I, type IconProps } from '@/components/ri/icons';
import { TabBadge } from '@/components/ri/TabBadge';
import type { StatusTone } from '@/components/ri/statusTones';

export type MobileTab = 'home' | 'accounts' | 'quotes' | 'approvals' | 'more';

export interface MobileTabItem<K extends string = string> {
  k: K;
  label: string;
  icon: ReactNode;
  badge?: number;
  badgeTone?: StatusTone;
  // The tab's primary action (event mode's Scan): an accent icon pill on the shared baseline, soft
  // while another tab is open and filled only when it is the current tab.
  primary?: boolean;
  ariaLabel?: string;
}

export interface MobileTabBarProps<K extends string = MobileTab> {
  active?: K;
  badges?: Partial<Record<MobileTab, number>>;
  showAccounts?: boolean;
  showApprovals?: boolean;
  onChange?: (tab: K) => void;
  tabs?: MobileTabItem<K>[];
}

const TABS: Array<{ k: MobileTab; label: string; icon: ReactElement<IconProps> }> = [
  { k: 'home', label: 'Home', icon: I.home as ReactElement<IconProps> },
  { k: 'quotes', label: 'Quotes', icon: I.doc as ReactElement<IconProps> },
  { k: 'approvals', label: 'Approve', icon: I.shield as ReactElement<IconProps> },
  { k: 'accounts', label: 'Accounts', icon: I.bldg as ReactElement<IconProps> },
  { k: 'more', label: 'More', icon: I.menu as ReactElement<IconProps> },
];

function sizedIcon(icon: ReactNode, size: number, stroke: number) {
  return isValidElement(icon) ? cloneElement(icon as ReactElement<IconProps>, { size, stroke }) : icon;
}

export function MobileTabBar<K extends string = MobileTab>({
  active,
  badges = {},
  showAccounts = false,
  showApprovals = false,
  onChange,
  tabs,
}: MobileTabBarProps<K>) {
  // `tabs` replaces the standard app tabs; badges/showAccounts/showApprovals only shape the defaults.
  const items: MobileTabItem<K>[] =
    tabs ??
    (TABS.filter((tab) => {
      if (tab.k === 'accounts') return showAccounts;
      if (tab.k === 'approvals') return showApprovals;
      return true;
    }).map((tab) => ({
      ...tab,
      badge: badges[tab.k],
      badgeTone: tab.k === 'approvals' ? 'warning' : 'danger',
    })) as MobileTabItem<K>[]);
  const current = active ?? (tabs ? undefined : ('home' as K));
  // With a primary pill every icon sits in a box of the pill's height, so icons and labels share
  // one baseline across the bar; the standard tabs keep their plain 22px icons.
  const iconBox = items.some((t) => t.primary) ? 28 : undefined;
  return (
    <div
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: 56,
        background: 'var(--surface-raised)',
        borderTop: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'stretch',
        zIndex: 10,
      }}
    >
      {items.map((t) => {
        const a = t.k === current;
        return (
          <button
            key={t.k}
            type="button"
            aria-current={a ? 'page' : undefined}
            aria-label={t.ariaLabel}
            onClick={() => onChange?.(t.k)}
            style={{
              flex: 1,
              background: 'transparent',
              border: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              padding: '6px 0',
              color: a ? 'var(--accent)' : 'var(--text-tertiary)',
              cursor: 'pointer',
              position: 'relative',
            }}
          >
            {t.primary ? (
              <span
                aria-hidden="true"
                data-tab-pill=""
                style={{
                  width: 48,
                  height: iconBox,
                  borderRadius: 999,
                  background: a ? 'var(--accent)' : 'var(--accent-soft)',
                  color: a ? 'var(--text-on-accent)' : 'var(--accent-text)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: a ? 'var(--shadow-xs)' : undefined,
                }}
              >
                {sizedIcon(t.icon, 20, 2)}
              </span>
            ) : (
              <div style={{ position: 'relative', height: iconBox, display: iconBox ? 'flex' : undefined, alignItems: 'center' }}>
                {sizedIcon(t.icon, 22, a ? 2 : 1.6)}
                {t.badge != null && <TabBadge tone={t.badgeTone ?? 'danger'}>{t.badge}</TabBadge>}
              </div>
            )}
            <span style={{ fontSize: 10.5, fontWeight: a ? 600 : 500 }}>{t.label}</span>
          </button>
        );
      })}
    </div>
  );
}
